<template>
  <div style="width: 100%; height: 100%;">
    <bean-source-page
      ref="sourcePage"
      :type="type"
      :columns="columns"
      :form-columns="formColumns"
      :filter-columns="filterColumns"
      :actions="actions"
      :form="form"
      :title="$t('model.payment')"
      resource="payments"
      :batch-actions="batchActions"
      :table-props="{
        'show-summary': true,
        'summary-method': this.summaryMethod
      }"
    >
      <template slot="action">
        <bean-import-button
          can='payments.create'
          buttonText="导入"
          url="/payments/import"
          :loadingToast="$t('importLoadingToast')"
          :fileChangeHandler="handleImportFileChange('/payments/import')"
        >
          <template slot="download">
            <el-popover trigger="hover">
              <div style="cursor: pointer;" @click="downloadImportTemplate('/payments/import_template')">下载模板</div>
              <i class="el-icon-info btn-tip-trigger" slot="reference" @click.stop></i>
            </el-popover>
          </template>
        </bean-import-button>
        <bean-import-button
          can='payments.audit'
          buttonText="支付宝流水"
          url="/payments/audit_by_alipay"
          :loadingToast="$t('importLoadingToast')"
          :fileChangeHandler="handleImportFileChange('/payments/audit_by_alipay')"
        />
        <bean-import-button
          can='payments.audit'
          buttonText="中行流水"
          url="/payments/audit_by_bank_of_china"
          fileAccept=".xlsx,.xls"
          :loadingToast="$t('importLoadingToast')"
          :fileChangeHandler="handleImportFileChange('/payments/audit_by_bank_of_china')"
        />
      </template>
    </bean-source-page>
  </div>
</template>

<script>
import { Vue, Component, Prop, Mixins, Emit } from 'vue-property-decorator';
import { filterColumnsExtend, columnsExtend, formColumnsExtend } from '../admin_grapes/payments.js';
import { columnsExtend as paymentAllocationColumnsExtend } from '../admin_entities/payment_allocation_detail.js';
import Utils from '../components/utils.vue';
import { displayText } from '@/custom_models_extend';
import _ from 'lodash';
import PayablesPayments from '@/components/payables-payments.vue';

@Component
export default class Payments extends Mixins(Vue, Utils) {
  @Prop(String) type;

  // form 新建表单初始化数据（default数据）
  form = {}

  formatAmount(value) {
    return value.toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  summaryMethod({ columns, data }) {
    return columns.map((column, index) => {
      if (index === 0) {
        return '合计';
      }
      if (column.property === 'amount') {
        return this.formatAmount(_.sumBy(data, item => Number(item.amount) || 0));
      }
      if (column.property === 'unallocated_amount') {
        return this.formatAmount(_.sumBy(data, item => Number(item.unallocated_amount) || 0));
      }
      return '';
    });
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - page type enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -RESTful api resource name
   */
  filterColumns() {
    const array = [
      'number_cont',
      'serial_number_cont',
      'customer_id_eq',
      {
        key: 'creator_id_eq',
        renderCell: {
          props: {
            clearable: true,
            xRemotePreload: async () => {
              return (await this.$request.get('users/read_options', { per_page: 100, ...this.userOptionsFilter })).data.map(item => ({ value: item.id, label: displayText(item) }));
            },
            xRemoteSearch: async (keyword) => {
              return (await this.$request.get('users/read_options', { keyword, ...this.userOptionsFilter })).data.map(item => ({ value: item.id, label: displayText(item) }));
            }
          },
        }
      },
      'kind_eq',
      'state_eq',
      'payment_account_id_eq',
      {
        prop: 'payment_allocations_payable_of_JushuitanOrder_type_o_id_eq',
        label: '聚水潭订单',
        renderCell: {
          component: 'select',
          props: {
            clearable: true,
            xRemotePreload: async () => {
              return (await this.$request.get('/jushuitan_orders/read_options', { per_page: 100 })).data.map(item => ({ value: item.o_id, label: displayText(item) }));
            },
            xRemoteSearch: async (keyword) => {
              return (await this.$request.get('/jushuitan_orders/read_options', { keyword })).data.map(item => ({ value: item.o_id, label: displayText(item) }));
            }
          }
        }
      },
      {
        prop: 'payment_allocations_payable_of_SalesOrder_type_number_eq',
        label: '销售订单',
        renderCell: {
          component: 'select',
          props: {
            clearable: true,
            xRemotePreload: async () => {
              return (await this.$request.get('/sales_orders/read_options', { per_page: 100 })).data.map(item => ({ value: item.number, label: displayText(item) }));
            },
            xRemoteSearch: async (keyword) => {
              return (await this.$request.get('/sales_orders/read_options', { keyword })).data.map(item => ({ value: item.number, label: displayText(item) }));
            }
          }
        }
      },
      {
        prop: 'unallocated',
        label: '未分配完全',
        renderCell: {
          component: 'switch',
        }
      },
      {
        key: 'paid_on_month_eq',
        label: '付款月份',
        renderCell: {
        component: 'datePicker',
        props: {
            valueFormat: 'yyyy-MM',
            type: 'month'
          }
        }
      },
    ].filter(Boolean)

    return filterColumnsExtend(array)
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - page type enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -RESTful api resource name
   */
  columns(h, { type }) {
    const array = [
      type === 'show' && 'id',
      { key: 'number', width: 180 },
      'customer',
      'kind',
      'state',
      'payment_account',
      'serial_number',
      {
        key: 'amount',
        width: 120,
        renderCell(h, { props: { value, scope: { row } } }) {
          return <span style={{ color: row.is_even ? 'black' : 'red' }}>{value}</span>
        }
      },
      {
        key: 'unallocated_amount',
        width: 120,
        renderCell(h, { props: { value, scope: { row } } }) {
          return <span style={{ color: row.is_even ? 'black' : 'red' }}>{value}</span>
        }
      },
      { key: 'paid_on', width: 120 },
      'image',
      {
        key: 'payment_allocations',
        width: 360,
        renderCell(h, { props: { value } }) {
          return <bean-table value={value} height={null} columns={paymentAllocationColumnsExtend(['payable', 'amount'])} />;
        }
      },
      type === 'show' && { key: 'remark' },
      'creator',
      'auditor',
      type === 'show' && 'created_at',
      type === 'show' && 'updated_at',
    ].filter(Boolean)

    return columnsExtend(array)
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - page type enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource - RESTful api resoure name
   * @param {Object} params.data - current form data
   * @param {Function} params.change - invoke this method to sync form change({ field: 'new value' })
   */
  formColumns(h, { data, type }) {
    let array = [
      {
        key: 'customer_id',
        renderCell: {
          props: {
            options: this.defaultCustomerOptions,
            xRemotePreload: async () => {
              return (await this.$request.get('customers/read_options', { per_page: 100, user_id_eq: this.currentUser.id, ...this.customerOptionsFilter })).data.map(item => ({ value: item.id, label: item.name }));
            },
            xRemoteSearch: async (keyword) => {
              return (await this.$request.get('customers/read_options', { keyword, user_id_eq: this.currentUser.id, ...this.customerOptionsFilter })).data.map(item => ({ value: item.id, label: item.name }));
            }
          },
          domProps: {
            style: 'width: 100%'
          }
        }
      },
      { key: 'kind', renderCell: { required: true } },
      {
        key: 'payment_account_id',
        renderCell: {
          props: {
            xRemotePreload: async () => {
              return (await this.$request.get('payment_accounts/read_options', { state_eq: 'active', per_page: 100 })).data.map(item => ({ value: item.id, label: displayText(item) }));
            },
            xRemoteSearch: async (keyword) => {
              return (await this.$request.get('payment_accounts/read_options', { state_eq: 'active', keyword })).data.map(item => ({ value: item.id, label: displayText(item) }));
            }
          }
        }
      },
      {
        key: 'image',
        renderCell: {
          props: {
            clipboard: true,
            drag: true,
          }
        }
      },
      {
        key: 'serial_number',
        renderCell: {
          component: (h, context) => {
            return <el-input {...context} disabled={data.state === 'approved' && this.roleKind !== 'finance'}>
              <el-button slot="append" icon="el-icon-zoom-in" onClick={() => {
                this.handleDetectFileChange(data)
              }}></el-button>
            </el-input>
          }
        }
      },
      'amount',
      'paid_on',
      data.customer_id && {
        key: 'payment_allocations',
        renderCell: {
          component: (h, context) => (
            <PayablesPayments
              {...context}
              type={type}
              serial_number={data.serial_number}
              customer_id={data.customer_id}
              record_type={data.record_type}
            />
          ),
        }
      },
      'remark',
    ].filter(Boolean)

    const result = formColumnsExtend(array, { data: data, type: type })

    if (data.state === 'approved' && this.roleKind !== 'finance') {
      _.each(result, item => {
        if (item['prop'] !== 'payment_allocations') {
          if (_.get(item, 'renderCell.props')) {
            item['renderCell']['props']['disabled'] = true
          }
        }
      })
    }

    return result
  }

  @Emit()
  async handleDetectFileChange(data) {
    const signedID = _.get(data, 'image')
    if (signedID) {
      const result = await this.$autoLoading(this.$request.post('/payments/detect', { image: signedID }, { timeout: 1 * 60 * 1000 }))
      this.$refs.sourcePage.syncForm({ serial_number: result.serial_number, amount: result.amount, paid_on: result.paid_on })
    }
  }

  /**
   * @param {Object} scope -页面/表格 scope 数据
   * @param {Object} scope.row - 详情页/表格行 数据
   * @param {Object} sourcePageScope
   * @param {Function} sourcePageScope.remove - sourcePage删除方法
   * @param {Function} sourcePageScope.reload - sourcePage重新请求页面数据
   */
  actions({ row }, { remove, reload }) {
    return [
      row.can_update && {
        component: 'routeButton',
        props: {
          label: this.$t('edit'),
          to: { name: 'payments.edit', params: { id: row.id } }
        }
      },
      row.can_destroy && {
        component: 'confirmButton',
        props: {
          label: this.$t('destroy'),
          type: 'danger',
          can: 'payments.destroy'
        },
        on: {
          confirm: () => remove(row.id)
        }
      },
      row.can_approve && {
        component: 'confirmButton',
        props: {
          label: this.$t('actions.payment.approve'),
          type: 'primary',
          can: 'payments.audit'
        },
        on: {
          confirm: async () => {
            await this.$autoLoading(this.$request.put(`/payments/${row.id}/approve`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      row.can_reject && {
        component: 'confirmButton',
        props: {
          label: this.$t('actions.payment.reject'),
          type: 'danger',
          can: 'payments.audit'
        },
        on: {
          confirm: async () => {
            await this.$autoLoading(this.$request.put(`/payments/${row.id}/reject`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      row.can_pend && {
        component: 'confirmButton',
        props: {
          label: this.$t('actions.payment.pend'),
          type: 'primary',
          can: 'payments.pend'
        },
        on: {
          confirm: async () => {
            await this.$autoLoading(this.$request.put(`/payments/${row.id}/pend`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      }
    ].filter(Boolean);
  }

  batchActions({ selection }, { resource }) {
    const exportClick = async (selection) => {
      if (selection.length === 0) {
        await this.$autoLoading(this.$request.get(`/${resource}/export`, { ...this.$route.query }));
      } else {
        await this.$autoLoading(this.$request.get(`/${resource}/export`, { ...this.$route.query, id_in: _.map(selection, item => item.id) }));
      }

      this.$message.success(this.$t('exporting'));
    }
    const canApprove = _.filter(selection, item => item.can_approve)
    const canReject = _.filter(selection, item => item.can_reject)

    return [
      () => <el-button type='primary' onClick={() => exportClick(selection)}><i class='el-icon-download'></i>{this.$t('export')}</el-button>,
      {
        component: 'button',
        props: {
          label: canApprove.length === 0 ? '通过' : `通过 (${canApprove.length})`,
          type: 'primary',
          disabled: canApprove.length === 0,
          can: 'payments.audit'
        },
        on: {
          click: async () => {
            await this.$confirm(`确定通过 ${_.join(_.map(canApprove, 'number'), ', ')}?`)
            await this.$autoLoading(this.$request.put('/payments/approve', { id_in: _.map(canApprove, 'id') }));
            this.$message.success(this.$t('operateSuccess'));
            window.location.reload();
          }
        }
      },
      {
        component: 'button',
        props: {
          label: canReject.length === 0 ? '拒绝' : `拒绝 (${canReject.length})`,
          type: 'primary',
          disabled: canReject.length === 0,
          can: 'payments.audit'
        },
        on: {
          click: async () => {
            await this.$confirm(`确定拒绝 ${_.join(_.map(canReject, 'number'), ', ')}?`)
            await this.$autoLoading(this.$request.put('/payments/reject', { id_in: _.map(canReject, 'id') }));
            this.$message.success(this.$t('operateSuccess'));
            window.location.reload();
          }
        }
      }
    ]
  }
}

export const route = [
  {
    path: '/payments',
    name: 'payments.index',
    component: Payments,
    props: {
      type: 'index',
    }
  },
  {
    path: '/payments/new',
    name: 'payments.new',
    component: Payments,
    props: {
      type: 'new',
    }
  },
  {
    path: '/payments/:id',
    name: 'payments.show',
    component: Payments,
    props: {
      type: 'show',
    }
  },
  {
    path: '/payments/:id/edit',
    name: 'payments.edit',
    component: Payments,
    props: {
      type: 'edit'
    }
  }
]
</script>
